import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import LocationModal from "../components/LocationModal";
import { COLOR, FONT } from "./mobiletheme";

/**
 * FIX NOTES (kept from previous pass):
 * 1. localStorage reads/writes wrapped so the component never crashes in
 *    private/incognito mode.
 * 2. Truncation handled by CSS ellipsis only (no double truncation).
 *
 * DESIGN: matches the new gradient header — a compact "shipping label"
 * chip instead of a plain text row, so it reads as tappable at a glance.
 */
const safeGet = (key, fallback = "") => {
  try {
    return localStorage.getItem(key) || fallback;
  } catch (_) {
    return fallback;
  }
};

const safeSet = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (_) {
    // localStorage unavailable — fail silently, in-memory state still updates
  }
};

export default function MobileLocation() {
  const { t } = useTranslation();
  const [address, setAddress] = useState(() => safeGet("user_address", t("location.setLocation")));
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const handleStorageChange = () => {
      const saved = safeGet("user_address", "");
      if (saved) setAddress(saved);
    };
    // Lets ServiceabilityGate.jsx (Mobile Accessories) open this same
    // modal instead of building a second location picker.
    const handleOpenRequest = () => setShowModal(true);

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("sb-open-location-modal", handleOpenRequest);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("sb-open-location-modal", handleOpenRequest);
    };
  }, []);

  const handleSave = (newAddr, addressObj) => {
    setAddress(newAddr);
    safeSet("user_address", newAddr);
    if (addressObj) {
      try {
        safeSet("user_address_detail", JSON.stringify(addressObj));
      } catch (_) {
        // ignore serialization failures, the display text above still saved
      }
    }
    setShowModal(false);
    window.dispatchEvent(new Event("locationUpdated"));
  };

  return (
    <>
      <div
        style={styles.container}
        className="sb-tap"
        onClick={() => setShowModal(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && setShowModal(true)}
      >
        <span style={styles.pinDot} />
        <div style={styles.textGroup} className="sb-location-text">
          <span style={styles.deliverTo} className="sb-location-deliver-to"> {t("location.deliverTo")} </span>
          <span style={styles.address} className="sb-location-address">
            <span style={styles.addressText} className="sb-location-address">{address}</span>
            <span style={styles.arrowIcon}>▾</span>
          </span>
        </div>
      </div>

      {showModal && (
        <LocationModal
          onClose={() => setShowModal(false)}
          onSelect={handleSave}
        />
      )}
    </>
  );
}

const styles = {
  container: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: COLOR.headerText,
    padding: "5px 12px 5px 4px",
    cursor: "pointer",
    borderRadius: "10px",
    minWidth: 0,
    width: "100%",
    boxSizing: "border-box",
    background: "transparent",
  },
  pinDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: COLOR.primary,
    boxShadow: `0 0 0 3px ${COLOR.primarySoft}`,
    flexShrink: 0,
    marginLeft: "6px",
  },
  textGroup: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    gap: "2px",
  },
  deliverTo: {
    opacity: 0.85,
    fontSize: "9.5px",
    fontFamily: FONT.mono,
    fontWeight: 600,
    letterSpacing: "0.12em",
  },
  address: {
    fontWeight: "700",
    fontSize: "13px",
    fontFamily: FONT.body,
    display: "flex",
    alignItems: "center",
    gap: "4px",
    minWidth: 0,
  },
  addressText: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
  },
  arrowIcon: {
    fontSize: "12px",
    opacity: 0.8,
    flexShrink: 0,
  }
};